export const CONSISTENCY_MODEL_VERSION = 1;
export const CONSISTENCY_START_SCORE = 50;
export const CONSISTENCY_EASING = 0.25;
export const WEEKLY_CONSISTENCY_TARGETS = [15, 45, 60, 75, 85, 92, 97, 100];

export function normalizeDateOnly(value) {
  const trimmed = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : '';
}

export function parseDateOnlyUtc(value) {
  const normalized = normalizeDateOnly(value);
  if (!normalized) return null;
  const parsed = new Date(`${normalized}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDateOnlyUtc(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export function getWeekStartForDate(date) {
  const normalized = new Date(date);
  normalized.setUTCHours(0, 0, 0, 0);
  const dayOfWeek = normalized.getUTCDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  normalized.setUTCDate(normalized.getUTCDate() - diff);
  return formatDateOnlyUtc(normalized);
}

export function getWeekStartForDateOnly(value) {
  const parsed = parseDateOnlyUtc(value);
  if (!parsed) return '';
  return getWeekStartForDate(parsed);
}

export function addDaysToDateOnly(value, days) {
  const parsed = parseDateOnlyUtc(value);
  if (!parsed) return '';
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return formatDateOnlyUtc(parsed);
}

export function addWeeksToDateOnly(value, weeks) {
  return addDaysToDateOnly(value, weeks * 7);
}

export function getTodayDateOnlyUtc(date = new Date()) {
  return formatDateOnlyUtc(new Date(date));
}

export function getLatestEvaluableWeek(date = new Date()) {
  const today = getTodayDateOnlyUtc(date);
  const currentWeekStart = getWeekStartForDateOnly(today);
  return addWeeksToDateOnly(currentWeekStart, -1);
}

export function getWeeklyConsistencyTarget(activeDays) {
  const safeDays = Math.max(0, Math.min(7, Math.floor(Number(activeDays) || 0)));
  return WEEKLY_CONSISTENCY_TARGETS[safeDays];
}

export function applyWeeklyConsistencyScore(currentScore, activeDays) {
  const numericScore = Number(currentScore);
  const safeScore = Math.max(
    0,
    Math.min(100, Number.isFinite(numericScore) ? numericScore : CONSISTENCY_START_SCORE)
  );
  const targetScore = getWeeklyConsistencyTarget(activeDays);
  const nextScore = safeScore + CONSISTENCY_EASING * (targetScore - safeScore);
  return Math.max(0, Math.min(100, Number(nextScore.toFixed(2))));
}

export function isMeaningfulActivityDay(day) {
  if (!day || typeof day !== 'object') return false;
  if (Array.isArray(day.lessonsCompleted) && day.lessonsCompleted.length > 0) return true;
  if (Number(day.exercisesCompleted || 0) > 0) return true;
  if (Number(day.wordsReviewed || 0) > 0) return true;
  if (Array.isArray(day.readerStoriesQualified) && day.readerStoriesQualified.length > 0) return true;
  return false;
}

export function countMeaningfulActiveDaysForWeek(activityLog, weekStart) {
  const log = activityLog || {};
  let activeDays = 0;

  for (let offset = 0; offset < 7; offset += 1) {
    const dateKey = addDaysToDateOnly(weekStart, offset);
    if (!dateKey) continue;
    if (isMeaningfulActivityDay(log[dateKey])) {
      activeDays += 1;
    }
  }

  return activeDays;
}

export function evaluateConsistencyState({
  activityLog,
  consistencyScore,
  consistencyLastEvaluatedWeek,
  today = new Date(),
} = {}) {
  const latestEvaluableWeek = getLatestEvaluableWeek(today);
  const numericScore = Number(consistencyScore);
  const startingScore = Math.max(
    0,
    Math.min(100, Number.isFinite(numericScore) ? numericScore : CONSISTENCY_START_SCORE)
  );
  const storedLastEvaluatedWeek = normalizeDateOnly(consistencyLastEvaluatedWeek);

  if (!latestEvaluableWeek) {
    return {
      consistencyScore: startingScore,
      consistencyLastEvaluatedWeek: storedLastEvaluatedWeek || '',
      consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
    };
  }

  if (!storedLastEvaluatedWeek) {
    return {
      consistencyScore: startingScore,
      consistencyLastEvaluatedWeek: latestEvaluableWeek,
      consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
    };
  }

  const normalizedLastEvaluatedWeek = getWeekStartForDateOnly(storedLastEvaluatedWeek);
  if (!normalizedLastEvaluatedWeek || normalizedLastEvaluatedWeek >= latestEvaluableWeek) {
    return {
      consistencyScore: startingScore,
      consistencyLastEvaluatedWeek: normalizedLastEvaluatedWeek || latestEvaluableWeek,
      consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
    };
  }

  let nextScore = startingScore;
  let weekCursor = addWeeksToDateOnly(normalizedLastEvaluatedWeek, 1);

  while (weekCursor && weekCursor <= latestEvaluableWeek) {
    const activeDays = countMeaningfulActiveDaysForWeek(activityLog, weekCursor);
    nextScore = applyWeeklyConsistencyScore(nextScore, activeDays);
    weekCursor = addWeeksToDateOnly(weekCursor, 1);
  }

  return {
    consistencyScore: nextScore,
    consistencyLastEvaluatedWeek: latestEvaluableWeek,
    consistencyModelVersion: CONSISTENCY_MODEL_VERSION,
  };
}

export function getConsistencyPhraseKey(score, activeDays) {
  if (score >= 85) return 'deepRhythm';
  if (score >= 50 && activeDays >= 5) return 'buildingMomentum';
  if (score >= 50) return 'holdingSteady';
  if (score >= 35) return 'gettingBackOnTrack';
  return 'quieterWeek';
}

export function getDayIntensity(entry) {
  if (!entry || typeof entry !== 'object') return 0;
  const lessons = entry.lessonsCompleted?.length ?? 0;
  const exercises = entry.exercisesCompleted ?? 0;
  const words = entry.wordsReviewed ?? 0;
  const readers = entry.readerStoriesQualified?.length ?? 0;
  const raw = lessons * 0.35 + exercises * 0.04 + words * 0.02 + readers * 0.25;
  return Math.min(1, raw);
}

export function getRecentConsistencyDays(activityLog, { locale, today = new Date() } = {}) {
  const days = [];
  const todayKey = getTodayDateOnlyUtc(today);

  for (let offset = 6; offset >= 0; offset -= 1) {
    const dateKey = addDaysToDateOnly(todayKey, -offset);
    const parsed = parseDateOnlyUtc(dateKey);
    const intensity = getDayIntensity(activityLog?.[dateKey]);
    const isToday = offset === 0;
    const active = intensity > 0;

    days.push({
      dateKey,
      active,
      intensity,
      isToday,
      label: parsed
        ? parsed
            .toLocaleDateString(locale || undefined, { weekday: 'short', timeZone: 'UTC' })
            .slice(0, 1)
            .toUpperCase()
        : '',
      state: active ? 'done' : isToday ? 'today' : 'empty',
      todayLabel: parsed ? String(parsed.getUTCDate()) : '',
    });
  }

  return days;
}
